import type {
  CalendarPost,
  PostPlatform,
  PostStatus,
  PostSummary,
  PostSummaryTarget,
} from '../../types/publishing.js';
import { effectiveTargetScheduledFor } from './destinations.js';

// Publishing — pure calendar bucketing + filtering for the drag-drop content
// calendar (Slice D). No I/O, no Date.now (callers pass the anchor date). The
// calendar reads the LOCAL posts mirror; these helpers shape it for the grid.

// Local 'YYYY-MM-DD' key for a date (the calendar buckets by wall-clock day).
export function dateKey(d: Date): string {
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

export function postDateKey(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : dateKey(d);
}

// A post appears on the calendar grid when it has a scheduledFor (scheduled,
// publishing, published, partial, or a draft the AM parked on a date). Drafts with
// no date live in the "unscheduled" tray.
export function isUnscheduled(p: PostSummary): boolean {
  return p.scheduledFor === null;
}

// The set of statuses that render as an editable/movable card on the calendar.
export const CALENDAR_STATUSES: PostStatus[] = [
  'draft',
  'scheduled',
  'publishing',
  'published',
  'partial',
  'failed',
];

export interface CalendarFilters {
  kyckAccountIds?: string[];
  platforms?: PostPlatform[];
  statuses?: PostStatus[];
  search?: string;
}

// Generic over the post type so the element type SURVIVES the filter: filtering a
// PostSummary[] still yields PostSummary[] (which the kyck-only tray needs), while
// the same function filters external posts. A non-generic CalendarPost[] signature
// would widen our own posts on the way through and lose the tray's typing.
export function filterPosts<T extends CalendarPost>(posts: T[], filters: CalendarFilters): T[] {
  const accSet = filters.kyckAccountIds && filters.kyckAccountIds.length > 0
    ? new Set(filters.kyckAccountIds)
    : null;
  const platSet = filters.platforms && filters.platforms.length > 0
    ? new Set(filters.platforms)
    : null;
  const statusSet = filters.statuses && filters.statuses.length > 0
    ? new Set(filters.statuses)
    : null;
  const search = (filters.search ?? '').trim().toLowerCase();
  return posts.filter((p) => {
    if (accSet && !accSet.has(p.kyckAccountId)) return false;
    if (platSet && !p.platforms.some((pl) => platSet.has(pl))) return false;
    if (statusSet && !statusSet.has(p.status)) return false;
    if (search && !p.content.toLowerCase().includes(search)) return false;
    return true;
  });
}

// Bucket the scheduled/dated posts by their local day key. Posts without a date
// are omitted (they belong in the unscheduled tray).
export function bucketByDay(posts: PostSummary[]): Map<string, PostSummary[]> {
  const out = new Map<string, PostSummary[]>();
  for (const p of posts) {
    const key = postDateKey(p.scheduledFor);
    if (!key) continue;
    const list = out.get(key);
    if (list) list.push(p);
    else out.set(key, [p]);
  }
  // Stable within-day order: earliest scheduledFor first.
  for (const list of out.values()) {
    list.sort((a, b) => (a.scheduledFor ?? '').localeCompare(b.scheduledFor ?? ''));
  }
  return out;
}

export function unscheduledPosts(posts: PostSummary[]): PostSummary[] {
  return posts
    .filter((p) => p.scheduledFor === null && p.status === 'draft')
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

// ---------------------------------------------------------------------------
// Per-destination fan-out (2026-07-19, extended 2026-07-20). A post going to
// Facebook feed, Instagram story and Google Business renders as THREE calendar
// lines — Agorapulse's pattern. One `Post` doc, N display rows, every row keyed
// back to the SAME post id.
//
// MULTI-PLACEMENT SHIPPED (2026-07-20) exactly as forward-planned: feed AND
// story to the same page arrive as ADDITIONAL entries in `targets[]`, and this
// expansion needed no change — it has always been strictly one row per targets[]
// ENTRY, never assuming at most one target per platform and never keying or
// deduping by platform.
//
// ⚠️ WHAT DID CHANGE (2026-07-20): a row now carries its OWN effective date.
// Per-destination scheduling means a post's destinations can sit on different
// days, so rows are bucketed INDIVIDUALLY — a post is no longer guaranteed to
// have all its rows in one cell. Consequently DRAGGING A ROW MOVES ONLY THAT
// DESTINATION; this supersedes the original fan-out rule ("dragging any row
// reschedules the whole post"), which was correct only while a post had a single
// scheduledFor. Clicking any row still opens the single editor with every
// destination editable.
// ---------------------------------------------------------------------------

export interface CalendarRow {
  // Unique per DISPLAY row (post id + target ordinal). Never use this as a post
  // id — but DO use it as the drag payload: a drag now targets one destination,
  // and the parent post id alone can no longer say which.
  rowKey: string;
  // The PARENT post — what a click resolves to. Always set, even for the
  // no-targets fallback row.
  postId: string;
  // CalendarPost, not PostSummary: a row can belong to a natively-published
  // (external) post we don't own. `post.origin` is what every mutation path gates
  // on — see isRowMovable.
  post: CalendarPost;
  // The destination this row represents; null only for the defensive
  // "post with no targets" fallback row.
  target: PostSummaryTarget | null;
  targetIndex: number;
  platform: PostPlatform | null;
  // 'Story' / 'Reel' / 'Offer' / 'Event' — the sub-placement, when it's
  // meaningfully distinct from the platform's default feed post. null = feed.
  placementLabel: string | null;
  // This ROW's effective time: the target's own scheduledFor when it has one,
  // else the post's. What the row is bucketed and moved by.
  scheduledFor: string | null;
  timeLabel: string;
  // true when this destination is on its own time rather than the post's — the
  // calendar marks it, so a post whose rows have visibly drifted apart is
  // explicable rather than looking like a bug.
  hasOwnTime: boolean;
}

// 'HH:MM' → '12:00pm'. Locale-independent (composed from numeric parts) and
// pure — matches the formatExactTimestamp posture in inboxView.
export function formatRowTime(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const h24 = d.getHours();
  const suffix = h24 < 12 ? 'am' : 'pm';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(d.getMinutes()).padStart(2, '0')}${suffix}`;
}

// The sub-placement label, when it differs from the platform's plain feed post.
// A FB *feed* post and a FB *story* must be tellable apart in the cell.
export function placementLabelFor(target: PostSummaryTarget | null): string | null {
  if (!target) return null;
  switch (target.platform) {
    case 'facebook':
      return target.fbContentType === 'story'
        ? 'Story'
        : target.fbContentType === 'reel'
          ? 'Reel'
          : null;
    case 'instagram':
      return target.igContentType === 'story'
        ? 'Story'
        : target.igContentType === 'reel'
          ? 'Reel'
          : null;
    case 'googlebusiness':
      return target.gbpTopicType === 'OFFER'
        ? 'Offer'
        : target.gbpTopicType === 'EVENT'
          ? 'Event'
          : null;
  }
}

/**
 * The post-level publish instant, but ONLY for a post that actually went out.
 *
 * This is the bucketing fallback that fixes publish-now posts: they carry no
 * `scheduledFor` (and no per-destination time), so without an anchor they bucket
 * on no day and disappear from the calendar despite publishing fine. A published/
 * partial post has a real `publishedAt` (stamped server-side), which is the honest
 * day to show it on. Anything not yet published (draft/scheduled/publishing) returns
 * null here — a draft belongs in the tray, and a scheduled post already has its
 * scheduledFor, so the fallback never applies to them.
 */
export function publishAnchor(post: CalendarPost): string | null {
  return post.status === 'published' || post.status === 'partial' ? post.publishedAt : null;
}

/**
 * The effective date a ROW buckets and displays on: the destination's own time,
 * then the post-level scheduledFor, then — for a dated-less PUBLISHED post — the
 * publish instant. The last leg is what keeps a publish-now post on the calendar.
 */
export function effectiveRowDate(
  post: CalendarPost,
  target: Pick<PostSummaryTarget, 'scheduledFor'> | null,
): string | null {
  const scheduled = target
    ? effectiveTargetScheduledFor(post.scheduledFor, target)
    : post.scheduledFor;
  return scheduled ?? publishAnchor(post);
}

/**
 * Expand one post into its display rows — one per `targets[]` entry.
 *
 * Defensive fallbacks, in order: `targets[]` (the real source) → `platforms[]`
 * (a legacy/stale summary that predates the targets projection) → a single
 * target-less row. A post is NEVER dropped from the calendar just because its
 * destination data is thin — an invisible post is undraggable and unfindable.
 */
export function expandPostToRows(post: CalendarPost): CalendarRow[] {
  const targets = post.targets ?? [];
  if (targets.length > 0) {
    return targets.map((target, i) => {
      const scheduledFor = effectiveRowDate(post, target);
      return {
        rowKey: rowKeyFor(post.id, i),
        postId: post.id,
        post,
        target,
        targetIndex: i,
        platform: target.platform,
        placementLabel: placementLabelFor(target),
        scheduledFor,
        timeLabel: formatRowTime(scheduledFor),
        hasOwnTime: !!target.scheduledFor,
      };
    });
  }
  // Both fallback branches use the post-level effective date (scheduledFor, else
  // the publish anchor) so a publish-now post with a thin projection still lands
  // on its published day rather than vanishing.
  const rowDate = effectiveRowDate(post, null);
  const timeLabel = formatRowTime(rowDate);
  const platforms = post.platforms ?? [];
  if (platforms.length > 0) {
    return platforms.map((platform, i) => ({
      rowKey: rowKeyFor(post.id, i),
      postId: post.id,
      post,
      target: { platform },
      targetIndex: i,
      platform,
      placementLabel: null,
      scheduledFor: rowDate,
      timeLabel,
      hasOwnTime: false,
    }));
  }
  return [
    {
      rowKey: rowKeyFor(post.id, 0),
      postId: post.id,
      post,
      target: null,
      targetIndex: 0,
      platform: null,
      placementLabel: null,
      scheduledFor: rowDate,
      timeLabel,
      hasOwnTime: false,
    },
  ];
}

// The row key is the drag payload, so it must round-trip back to (post, target).
export function rowKeyFor(postId: string, targetIndex: number): string {
  return `${postId}#${targetIndex}`;
}

export function parseRowKey(rowKey: string): { postId: string; targetIndex: number } | null {
  const hash = rowKey.lastIndexOf('#');
  if (hash <= 0) return null;
  const postId = rowKey.slice(0, hash);
  const targetIndex = Number(rowKey.slice(hash + 1));
  if (!Number.isInteger(targetIndex) || targetIndex < 0) return null;
  return { postId, targetIndex };
}

export function expandPostsToRows(posts: CalendarPost[]): CalendarRow[] {
  return posts.flatMap(expandPostToRows);
}

/**
 * Bucket the fanned rows by local day key — by EACH ROW's own effective date.
 *
 * ⚠️ This used to bucket POSTS and then fan each cell out, which quietly assumed
 * every row of a post shares one day. Per-destination scheduling (2026-07-20)
 * broke that assumption: a Facebook post on Monday with its story on Tuesday is
 * one post occupying two cells. Rows are therefore expanded FIRST and bucketed
 * individually. A row with no effective date is omitted (its post belongs in the
 * unscheduled tray).
 */
export function bucketRowsByDay(posts: CalendarPost[]): Map<string, CalendarRow[]> {
  const out = new Map<string, CalendarRow[]>();
  for (const row of expandPostsToRows(posts)) {
    const key = postDateKey(row.scheduledFor);
    if (!key) continue;
    const list = out.get(key);
    if (list) list.push(row);
    else out.set(key, [row]);
  }
  // Stable within-day order: earliest first, then by post + destination so a
  // post's own rows stay adjacent when they share a time.
  for (const list of out.values()) {
    list.sort(
      (a, b) =>
        (a.scheduledFor ?? '').localeCompare(b.scheduledFor ?? '') ||
        a.postId.localeCompare(b.postId) ||
        a.targetIndex - b.targetIndex,
    );
  }
  return out;
}

// How many rows a day cell renders before collapsing behind the "All (N posts)"
// affordance. Fan-out multiplies lines per day, so silently clipping is not an
// option (brief §D).
export const MAX_CELL_ROWS = 4;

// The distinct POSTS behind a cell's rows — the affordance counts posts, not
// rows ("All (3 posts)" when 3 posts fanned into 7 lines).
export function distinctPostCount(rows: CalendarRow[]): number {
  return new Set(rows.map((r) => r.postId)).size;
}

// A calendar month grid: 6 weeks × 7 days of date cells (leading days from the
// previous month + trailing from the next), so the grid is always rectangular.
export interface CalendarCell {
  date: Date;
  key: string;
  inMonth: boolean;
  isToday: boolean;
}

export function buildMonthGrid(year: number, month0: number, today: Date): CalendarCell[][] {
  const first = new Date(year, month0, 1);
  // Start on the Sunday on/before the 1st.
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const todayKey = dateKey(today);
  const weeks: CalendarCell[][] = [];
  const cursor = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week: CalendarCell[] = [];
    for (let d = 0; d < 7; d++) {
      const cell: CalendarCell = {
        date: new Date(cursor),
        key: dateKey(cursor),
        inMonth: cursor.getMonth() === month0,
        isToday: dateKey(cursor) === todayKey,
      };
      week.push(cell);
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

// Re-anchor a scheduledFor to a new day, preserving the wall-clock time of day.
// Used by drag-to-reschedule (dropping a card on a day). Returns a new ISO string.
export function rescheduleToDay(currentIso: string | null, targetDayKey: string): string {
  const [y, m, d] = targetDayKey.split('-').map((n) => Number(n));
  const src = currentIso ? new Date(currentIso) : null;
  const hours = src && !Number.isNaN(src.getTime()) ? src.getHours() : 9;
  const minutes = src && !Number.isNaN(src.getTime()) ? src.getMinutes() : 0;
  const next = new Date(y!, (m ?? 1) - 1, d ?? 1, hours, minutes, 0, 0);
  return next.toISOString();
}
